<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
const email = ref('')
const password = ref('')
const error = ref('')
onMounted(() => {
  // No session expiration messages - admin login is hidden
})
const loading = ref(false)
const router = useRouter()
async function login() {
  error.value = ''
  loading.value = true
  try {
    const form = new URLSearchParams()
    form.append('username', email.value)
    form.append('password', password.value)
    // Cookie is set automatically by backend (httpOnly)
    await axios.post('/api/auth/token', form, { withCredentials: true })

    // Use window.location to ensure fresh page load with new auth cookie
    // This prevents router guard race conditions
    window.location.href = '/admin/pages'
  } catch (e) {
    error.value = 'login_failed'
    loading.value = false
  }
}
</script>

<template>
  <div class="login-page">
    <div class="login-card">
      <div class="logo-area">
        <span class="lock-icon">🔒</span>
      </div>
      <h2>{{ $t('admin.loginTitle') }}</h2>

      <form @submit.prevent="login" class="login-form">
        <div class="form-group">
          <label for="email">{{ $t('admin.email') }}</label>
          <input id="email" v-model="email" type="email" required />
        </div>

        <div class="form-group">
          <label for="password">{{ $t('admin.password') }}</label>
          <input id="password" v-model="password" type="password" required />
        </div>

        <button class="btn-primary" :disabled="loading">
          {{ loading ? $t('common.loading') : $t('admin.login') }}
        </button>

        <div v-if="error" class="error-msg">
          {{
            $t(
              error === 'login_failed'
                ? 'admin.loginFailed'
                : error === 'auth_expired'
                  ? 'admin.authExpired'
                  : 'common.errorLoading'
            )
          }}
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.login-page {
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.login-card {
  background: var(--inner);
  border: 1px solid var(--border);
  padding: 2.5rem 2rem;
  border-radius: 20px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  text-align: center;
}

.logo-area {
  margin-bottom: 1rem;
}

.lock-icon {
  font-size: 2.5rem;
  background: rgba(224, 126, 39, 0.1);
  width: 80px;
  height: 80px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid rgba(224, 126, 39, 0.2);
}

h2 {
  margin: 0 0 0.5rem;
  font-size: 1.8rem;
  letter-spacing: 0.5px;
}

.subtitle {
  margin: 0 0 2rem;
  opacity: 0.7;
  font-size: 0.9rem;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  text-align: left;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-size: 0.85rem;
  font-weight: 600;
  opacity: 0.9;
  letter-spacing: 0.5px;
}

input {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.8rem 1rem;
  color: #fff;
  font-size: 1rem;
  transition: all 0.2s ease;
}

input:focus {
  outline: none;
  border-color: var(--accent);
  background: rgba(0, 0, 0, 0.4);
  box-shadow: 0 0 0 3px rgba(224, 126, 39, 0.15);
}

.btn-primary {
  margin-top: 0.5rem;
  background: var(--accent);
  color: #fff;
  border: none;
  padding: 0.9rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;
  letter-spacing: 0.5px;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(224, 126, 39, 0.3);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.error-msg {
  color: #ff4444;
  background: rgba(255, 68, 68, 0.1);
  padding: 0.8rem;
  border-radius: 8px;
  font-size: 0.9rem;
  text-align: center;
  border: 1px solid rgba(255, 68, 68, 0.2);
}
</style>
